/**
 * carTypeMatch.ts
 *
 * 予約の car_type（reservations.re_car_type）を車種マスタ（vehicle_types）に突き合わせる。
 *
 * 背景（2026-09-24）:
 *   re_car_type にはコード（'T-AV-H'）だけでなく名前（'アルファード30系'）が入っている
 *   予約が多数ある（2025-12 以降、月 100〜200 件ペースで継続）。従来 daylist の名前列は
 *   `ct.code === row.car_type` の厳密一致だったため、名前格納の予約は色が付かなかった。
 *
 * 突き合わせ順（先勝ち・既存挙動を維持するためコード優先）:
 *   1. code / vehicle_type_code
 *   2. name / vehicle_type_name
 *   3. id
 * 比較は trim + NFKC 正規化 + 大文字化した上で行う（全角英数・前後空白・大小文字の揺れを吸収）。
 */

export interface CarTypeLike {
  id?: string | number | null;
  code?: string | null;
  name?: string | null;
  vehicle_type_code?: string | null;
  vehicle_type_name?: string | null;
  [key: string]: unknown;
}

export const normalizeCarTypeKey = (value: unknown): string => {
  if (value == null) return '';
  const str = typeof value === 'string' ? value : String(value);
  const trimmed = str.trim();
  if (!trimmed) return '';
  return trimmed.normalize('NFKC').toUpperCase();
};

const pickKeys = (ct: CarTypeLike, fields: (keyof CarTypeLike)[]): string[] =>
  fields.map((f) => normalizeCarTypeKey(ct[f])).filter(Boolean);

/**
 * carTypesList から value（予約の car_type 等）に対応する車種を返す。見つからなければ null。
 */
export const findCarType = <T extends CarTypeLike>(
  carTypesList: readonly T[] | null | undefined,
  value: unknown,
): T | null => {
  const key = normalizeCarTypeKey(value);
  if (!key || !Array.isArray(carTypesList) || carTypesList.length === 0) return null;

  const byCode = carTypesList.find((ct) => ct && pickKeys(ct, ['code', 'vehicle_type_code']).includes(key));
  if (byCode) return byCode;

  const byName = carTypesList.find((ct) => ct && pickKeys(ct, ['name', 'vehicle_type_name']).includes(key));
  if (byName) return byName;

  const byId = carTypesList.find((ct) => ct && pickKeys(ct, ['id']).includes(key));
  return byId ?? null;
};
